import { useState, useRef, useEffect } from "react";

export const VideoBackground = ({ 
  src, 
  fallbackSrc,
  poster,
  overlay = "bg-black/50",
  className = "" 
}) => {
  const videoRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleCanPlay = () => setIsLoaded(true);
    const handleError = () => {
      setHasError(true);
      // Try fallback if available
      if (fallbackSrc && video.src !== fallbackSrc) {
        video.src = fallbackSrc;
      }
    };

    // Smooth loop transition, fade out before end, fade in after restart
    const handleTimeUpdate = () => {
      const timeRemaining = video.duration - video.currentTime;
      // Start fading 0.5 seconds before end
      if (timeRemaining <= 0.5 && timeRemaining > 0 && !isFading) {
        setIsFading(true);
      }
    };

    const handleSeeked = () => {
      // Video has looped back to start
      if (video.currentTime < 1) {
        // Small delay then fade back in
        setTimeout(() => setIsFading(false), 100);
      }
    };

    // Mobile autoplay fix - attempt to play on any user interaction
    const attemptPlay = () => {
      if (video.paused) {
        video.play().catch(() => {
          // Silently fail - video will show poster or first frame
        });
      }
    };

    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('seeked', handleSeeked);
    
    // Try to play immediately
    video.play().catch(() => {
      // If autoplay blocked, try on first user interaction
      document.addEventListener('touchstart', attemptPlay, { once: true });
      document.addEventListener('click', attemptPlay, { once: true });
      document.addEventListener('scroll', attemptPlay, { once: true });
    });

    return () => {
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('seeked', handleSeeked);
      document.removeEventListener('touchstart', attemptPlay);
      document.removeEventListener('click', attemptPlay);
      document.removeEventListener('scroll', attemptPlay);
    };
  }, [fallbackSrc, isFading]);

  return (
    <div className={`absolute inset-0 overflow-hidden ${className}`}>
      {/* Video element */}
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        webkit-playsinline="true"
        poster={poster}
        className={`absolute w-full h-full object-cover transition-opacity duration-500 ${
          isLoaded && !isFading ? 'opacity-100' : 'opacity-70'
        }`}
        data-testid="video-background"
      >
        <source src={src} type="video/mp4" />
      </video>

      {/* Gradient overlay for text readability */}
      <div className={`absolute inset-0 ${overlay}`} />

      {/* Additional brand gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-deep-violet/30 via-transparent to-deep-violet/60" />
    </div>
  );
};

// Split screen video background for special layouts
export const SplitVideoBackground = ({
  leftSrc,
  rightSrc,
  leftPoster,
  rightPoster,
  overlay = "bg-black/30"
}) => {
  const leftVideoRef = useRef(null);
  const rightVideoRef = useRef(null);
  const [leftLoaded, setLeftLoaded] = useState(false);
  const [rightLoaded, setRightLoaded] = useState(false);

  useEffect(() => {
    const leftVideo = leftVideoRef.current;
    const rightVideo = rightVideoRef.current;

    if (leftVideo) {
      leftVideo.addEventListener('canplay', () => setLeftLoaded(true));
    }
    if (rightVideo) {
      rightVideo.addEventListener('canplay', () => setRightLoaded(true));
    }
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Split container */}
      <div className="absolute inset-0 flex">
        {/* Left video, person talking */}
        <div className="relative w-1/2 h-full overflow-hidden">
          <video
            ref={leftVideoRef}
            autoPlay
            muted
            loop
            playsInline
            poster={leftPoster}
            className={`absolute w-full h-full object-cover transition-opacity duration-1000 ${
              leftLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <source src={leftSrc} type="video/mp4" />
          </video>
          <div className={`absolute inset-0 ${overlay}`} />
          {/* Warm tint for therapy side */}
          <div className="absolute inset-0 bg-brand-peach/10" />
        </div>

        {/* Right video, code typing */}
        <div className="relative w-1/2 h-full overflow-hidden">
          <video
            ref={rightVideoRef}
            autoPlay
            muted
            loop
            playsInline
            poster={rightPoster}
            className={`absolute w-full h-full object-cover transition-opacity duration-1000 ${
              rightLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <source src={rightSrc} type="video/mp4" />
          </video>
          <div className={`absolute inset-0 ${overlay}`} />
          {/* Cool tint for tech side */}
          <div className="absolute inset-0 bg-brand-primary/10" />
        </div>
      </div>

      {/* Center divider line */}
      <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-brand-peach/60 to-transparent" />

      {/* Overall gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-deep-violet/50 via-transparent to-deep-violet/70" />
    </div>
  );
};

// Tri-split screen video background for Why Become page
export const TriSplitVideoBackground = ({
  leftSrc,
  centerSrc,
  rightSrc,
  overlay = "bg-black/40"
}) => {
  const leftVideoRef = useRef(null);
  const centerVideoRef = useRef(null);
  const rightVideoRef = useRef(null);
  const [leftLoaded, setLeftLoaded] = useState(false);
  const [centerLoaded, setCenterLoaded] = useState(false);
  const [rightLoaded, setRightLoaded] = useState(false);

  useEffect(() => {
    const leftVideo = leftVideoRef.current;
    const centerVideo = centerVideoRef.current;
    const rightVideo = rightVideoRef.current;

    if (leftVideo) {
      leftVideo.addEventListener('canplay', () => setLeftLoaded(true));
    }
    if (centerVideo) {
      centerVideo.addEventListener('canplay', () => setCenterLoaded(true));
    }
    if (rightVideo) {
      rightVideo.addEventListener('canplay', () => setRightLoaded(true));
    }

    // Mobile autoplay fix
    const attemptPlay = () => {
      if (leftVideo?.paused) leftVideo.play().catch(() => {});
      if (centerVideo?.paused) centerVideo.play().catch(() => {});
      if (rightVideo?.paused) rightVideo.play().catch(() => {});
    };

    // Try to play immediately
    leftVideo?.play().catch(() => {});
    centerVideo?.play().catch(() => {});
    rightVideo?.play().catch(() => {});

    // If autoplay blocked, try on first user interaction
    document.addEventListener('touchstart', attemptPlay, { once: true });
    document.addEventListener('click', attemptPlay, { once: true });
    document.addEventListener('scroll', attemptPlay, { once: true });

    return () => {
      document.removeEventListener('touchstart', attemptPlay);
      document.removeEventListener('click', attemptPlay);
      document.removeEventListener('scroll', attemptPlay);
    };
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Tri-split container */}
      <div className="absolute inset-0 flex">
        {/* Left video, Working mom */}
        <div className="relative w-1/3 h-full overflow-hidden">
          <video
            ref={leftVideoRef}
            autoPlay
            muted
            loop
            playsInline
            webkit-playsinline="true"
            className={`absolute w-full h-full object-cover transition-opacity duration-1000 ${
              leftLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <source src={leftSrc} type="video/mp4" />
          </video>
          <div className={`absolute inset-0 ${overlay}`} />
          <div className="absolute inset-0 bg-brand-peach/10" />
        </div>

        {/* Center video, Early career dreamer */}
        <div className="relative w-1/3 h-full overflow-hidden">
          <video
            ref={centerVideoRef}
            autoPlay
            muted
            loop
            playsInline
            webkit-playsinline="true"
            className={`absolute w-full h-full object-cover transition-opacity duration-1000 ${
              centerLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <source src={centerSrc} type="video/mp4" />
          </video>
          <div className={`absolute inset-0 ${overlay}`} />
          <div className="absolute inset-0 bg-brand-lavender/10" />
        </div>

        {/* Right video, Experienced professional */}
        <div className="relative w-1/3 h-full overflow-hidden">
          <video
            ref={rightVideoRef}
            autoPlay
            muted
            loop
            playsInline
            webkit-playsinline="true"
            className={`absolute w-full h-full object-cover transition-opacity duration-1000 ${
              rightLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <source src={rightSrc} type="video/mp4" />
          </video>
          <div className={`absolute inset-0 ${overlay}`} />
          <div className="absolute inset-0 bg-brand-primary/10" />
        </div>
      </div>

      {/* Divider lines */}
      <div className="absolute left-1/3 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-white/30 to-transparent" />
      <div className="absolute left-2/3 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-white/30 to-transparent" />

      {/* Overall gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-deep-violet/60 via-deep-violet/30 to-deep-violet/80" />
    </div>
  );
};

// Dual-split video background (left + center only, no right panel)
export const DualSplitVideoBackground = ({
  leftSrc,
  centerSrc,
  overlay = "bg-black/40"
}) => {
  const leftVideoRef = useRef(null);
  const centerVideoRef = useRef(null);
  const [leftLoaded, setLeftLoaded] = useState(false);
  const [centerLoaded, setCenterLoaded] = useState(false);

  useEffect(() => {
    const leftVideo = leftVideoRef.current;
    const centerVideo = centerVideoRef.current;

    if (leftVideo) {
      leftVideo.addEventListener('canplay', () => setLeftLoaded(true));
    }
    if (centerVideo) {
      centerVideo.addEventListener('canplay', () => setCenterLoaded(true));
    }

    // Mobile autoplay fix
    const attemptPlay = () => {
      if (leftVideo?.paused) leftVideo.play().catch(() => {});
      if (centerVideo?.paused) centerVideo.play().catch(() => {});
    };

    // Try to play immediately
    leftVideo?.play().catch(() => {});
    centerVideo?.play().catch(() => {});

    // If autoplay blocked, try on first user interaction
    document.addEventListener('touchstart', attemptPlay, { once: true });
    document.addEventListener('click', attemptPlay, { once: true });
    document.addEventListener('scroll', attemptPlay, { once: true });

    return () => {
      document.removeEventListener('touchstart', attemptPlay);
      document.removeEventListener('click', attemptPlay);
      document.removeEventListener('scroll', attemptPlay);
    };
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Dual-split container */}
      <div className="absolute inset-0 flex">
        {/* Left video */}
        <div className="relative w-1/2 h-full overflow-hidden">
          <video
            ref={leftVideoRef}
            autoPlay
            muted
            loop
            playsInline
            webkit-playsinline="true"
            className={`absolute w-full h-full object-cover transition-opacity duration-1000 ${
              leftLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <source src={leftSrc} type="video/mp4" />
          </video>
          <div className={`absolute inset-0 ${overlay}`} />
          <div className="absolute inset-0 bg-brand-peach/10" />
        </div>

        {/* Center/Right video */}
        <div className="relative w-1/2 h-full overflow-hidden">
          <video
            ref={centerVideoRef}
            autoPlay
            muted
            loop
            playsInline
            webkit-playsinline="true"
            className={`absolute w-full h-full object-cover transition-opacity duration-1000 ${
              centerLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <source src={centerSrc} type="video/mp4" />
          </video>
          <div className={`absolute inset-0 ${overlay}`} />
          <div className="absolute inset-0 bg-brand-lavender/10" />
        </div>
      </div>

      {/* Divider line */}
      <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-white/30 to-transparent" />

      {/* Overall gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-deep-violet/60 via-deep-violet/30 to-deep-violet/80" />
    </div>
  );
};

// Video + Mockup split background for How It Works page
export const VideoMockupBackground = ({
  videoSrc,
  videoPoster,
  MockupComponent,
  overlay = "bg-black/30"
}) => {
  const videoRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleCanPlay = () => setVideoLoaded(true);
    
    // Smooth loop transition
    const handleTimeUpdate = () => {
      const timeRemaining = video.duration - video.currentTime;
      if (timeRemaining <= 0.5 && timeRemaining > 0 && !isFading) {
        setIsFading(true);
      }
    };

    const handleSeeked = () => {
      if (video.currentTime < 1) {
        setTimeout(() => setIsFading(false), 100);
      }
    };

    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('seeked', handleSeeked);

    return () => {
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('seeked', handleSeeked);
    };
  }, [isFading]);

  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Split container */}
      <div className="absolute inset-0 flex">
        {/* Left video, person */}
        <div className="relative w-1/2 h-full overflow-hidden">
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            poster={videoPoster}
            className={`absolute w-full h-full object-cover transition-opacity duration-500 ${
              videoLoaded && !isFading ? 'opacity-100' : 'opacity-70'
            }`}
          >
            <source src={videoSrc} type="video/mp4" />
          </video>
          <div className={`absolute inset-0 ${overlay}`} />
          {/* Warm tint */}
          <div className="absolute inset-0 bg-brand-peach/10" />
        </div>

        {/* Right side, Mockup Component */}
        <div className="relative w-1/2 h-full overflow-hidden bg-gradient-to-br from-[#1a1a3e] to-[#0f0f23]">
          <div className={`absolute inset-0 ${overlay} opacity-50`} />
          {MockupComponent && <MockupComponent />}
        </div>
      </div>

      {/* Center divider line */}
      <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-brand-peach/60 to-transparent" />

      {/* Overall gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-deep-violet/50 via-transparent to-deep-violet/70 pointer-events-none" />
    </div>
  );
};

